import { encodeBuyerId } from "./buyerIdObfuscation";

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const firstNonEmptyString = (...values) => {
    for (const value of values) {
        if (typeof value !== "string") continue;

        const trimmed = value.trim();
        if (trimmed !== "") {
            return trimmed;
        }
    }

    return "";
};

const summarizeChatMessage = (message = {}) => {
    const firstAttachment = Array.isArray(message?.attachments) ? message.attachments[0] : null;

    return firstNonEmptyString(
        message?.body,
        message?.message,
        message?.text,
        firstAttachment?.caption,
        firstAttachment?.original_name,
    ) || "Sent an attachment";
};

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

export const getChatNotificationTargets = (pageProps = {}) => {
    const targets = pageProps?.chatNotifications || {};

    return {
        enabled: !!targets.enabled,
        projects: Array.isArray(targets.projects) ? targets.projects : [],
        apartments: Array.isArray(targets.apartments) ? targets.apartments : [],
        threads: Array.isArray(targets.threads) ? targets.threads : [],
    };
};

export const getChatSocketBasePath = (pageProps = {}) =>
    `${pageProps?.user?.type || ""}`.toLowerCase() === "buyer" ? "/c" : "/a";

export const getChatSocketUrl = (pageProps = {}) => pageProps?.chat_socket_url;

export const fetchChatSocketContext = async (pageProps = {}) => {
    if (!window?.axios) {
        throw new Error("Axios is not available for chat socket initialization.");
    }

    const base = getChatSocketBasePath(pageProps);
    const [tokenRes, mutedRes] = await Promise.all([
        window.axios.post(`${base}/apartments/chat/socket-token`),
        window.axios.get(`${base}/apartments/chat/muted-threads`),
    ]);

    return {
        base,
        token: tokenRes?.data?.token || null,
        mutedThreadIds: (mutedRes?.data?.muted_thread_ids || []).map((id) => Number(id)),
    };
};

export const joinChatNotificationRooms = (socket, pageProps = {}, extraTargets = {}) => {
    if (!socket) return;

    const targets = getChatNotificationTargets(pageProps);
    const projects = [...targets.projects, ...(Array.isArray(extraTargets.projects) ? extraTargets.projects : [])];
    const apartments = [...targets.apartments, ...(Array.isArray(extraTargets.apartments) ? extraTargets.apartments : [])];
    const seenProjects = new Set();
    const seenApartments = new Set();

    projects.forEach((project) => {
        const projectId = toNumber(project?.id);
        if (!projectId || seenProjects.has(projectId)) return;

        seenProjects.add(projectId);
        socket.emit("project.join", { project_id: projectId });
    });

    apartments.forEach((apartment) => {
        const apartmentId = toNumber(apartment?.id);
        if (!apartmentId || seenApartments.has(apartmentId)) return;

        seenApartments.add(apartmentId);
        socket.emit("apartment.join", { apartment_id: apartmentId });
    });
};

export const findChatNotificationThread = (pageProps = {}, evt = {}) => {
    const targets = getChatNotificationTargets(pageProps);
    const threadId = toNumber(evt?.thread_id ?? evt?.threadId);
    const apartmentId = toNumber(evt?.apartment_id ?? evt?.apartmentId);
    const projectId = toNumber(evt?.project_id ?? evt?.projectId);

    return (
        targets.threads.find((thread) => {
            const currentThreadId = toNumber(thread?.id);
            const currentApartmentId = toNumber(thread?.apartment_id);
            const currentProjectId = toNumber(thread?.project_id);

            if (threadId) {
                return currentThreadId === threadId;
            }

            if (apartmentId && projectId) {
                return currentApartmentId === apartmentId && currentProjectId === projectId;
            }

            if (apartmentId) {
                return currentApartmentId === apartmentId;
            }

            return false;
        }) || null
    );
};

export const isRelevantChatNotification = (pageProps = {}, evt = {}) => {
    const targets = getChatNotificationTargets(pageProps);
    if (!targets.enabled) return false;

    const matchedThread = findChatNotificationThread(pageProps, evt);
    if (matchedThread) {
        return true;
    }

    const userType = `${pageProps?.user?.type || ""}`.toLowerCase();
    if (userType === "buyer") {
        const evtBuyerId = toNumber(evt?.buyer_id ?? evt?.buyerId);
        if (evtBuyerId) {
            return (
                evtBuyerId === toNumber(pageProps?.user?.buyer_id) ||
                targets.apartments.some((apartment) => toNumber(apartment?.buyer_id) === evtBuyerId)
            );
        }

        const evtUserId = toNumber(evt?.buyer_user_id ?? evt?.buyerUserId ?? evt?.user_id ?? evt?.userId);
        if (evtUserId) {
            return evtUserId === toNumber(pageProps?.user?.id);
        }

        const apartmentId = toNumber(evt?.apartment_id ?? evt?.apartmentId);
        if (apartmentId && targets.apartments.some((ap) => toNumber(ap?.id) === apartmentId)) {
            return true;
        }

        return false;
    }

    const apartmentId = toNumber(evt?.apartment_id ?? evt?.apartmentId);
    if (apartmentId && targets.apartments.some((apartment) => toNumber(apartment?.id) === apartmentId)) {
        return true;
    }

    const projectId = toNumber(evt?.project_id ?? evt?.projectId);
    return !!(projectId && targets.projects.some((project) => toNumber(project?.id) === projectId));
};

export const buildChatNotificationPayload = (pageProps = {}, evt = {}, routeFn = null, overrideThread = null) => {
    const targets = getChatNotificationTargets(pageProps);
    const threadId = toNumber(evt?.thread_id ?? evt?.threadId ?? evt?.thread?.id);
    const thread = overrideThread || findChatNotificationThread(pageProps, evt);
    const apartmentId = toNumber(evt?.apartment_id ?? evt?.apartmentId ?? evt?.apartment?.id);
    const projectId = toNumber(evt?.project_id ?? evt?.projectId ?? evt?.project?.id);
    const apartment = targets.apartments.find((item) => toNumber(item?.id) === apartmentId) || null;
    const project = targets.projects.find((item) => toNumber(item?.id) === projectId) || null;
    const userType = `${pageProps?.user?.type || ""}`.toLowerCase();
    const isBuyer = userType === "buyer";
    const buyerId = toNumber(
        evt?.buyer_id ?? evt?.buyerId ?? evt?.buyer?.id ?? (isBuyer ? apartment?.buyer_id : null)
    );
    const buyerFromList = apartment?.buyers?.find((b) => toNumber(b.id) === buyerId);

    const senderName =
        evt?.sender_name ||
        evt?.senderName ||
        evt?.sender?.name ||
        (isBuyer
            ? thread?.advisor_name || apartment?.advisor_name
            : thread?.buyer_name || buyerFromList?.name) ||
        "New Message";
    const firstAttachment = Array.isArray(evt?.attachments) ? evt.attachments[0] : null;
    const meta = evt?.meta || evt?.meta_json || {};
    const threadUuid =
        evt?.thread_uuid ||
        evt?.threadUuid ||
        evt?.thread?.uuid ||
        thread?.uuid ||
        null;
    const apartmentUuid =
        evt?.apartment_uuid ||
        evt?.apartmentUuid ||
        evt?.apartment?.uuid ||
        apartment?.uuid ||
        null;

    const message = firstNonEmptyString(
        evt?.body,
        evt?.message,
        evt?.text,
        evt?.preview,
        evt?.content,
        meta?.body,
        firstAttachment?.caption,
        firstAttachment?.original_name
    ) || "Sent an attachment";
    const projectName = evt?.project_name || evt?.projectName || thread?.project_name || apartment?.project_name || project?.name || "";
    const apartmentName = evt?.apartment_name || evt?.apartmentName || thread?.apartment_name || apartment?.name || "";

    return {
        title: senderName,
        message,
        text: [projectName, apartmentName, message].filter(Boolean).join(" | "),
        url: buildChatNotificationUrl(pageProps, evt, routeFn, thread),
        apartmentId,
        projectId,
        threadId,
        threadUuid,
        apartmentUuid,
        buyerId,
        thread,
        apartment,
        project,
    };
};

export const buildChatNotificationEventKey = (evt = {}) => {
    const explicitId = evt?.message_id ?? evt?.messageId ?? evt?.id;
    if (explicitId !== undefined && explicitId !== null && `${explicitId}` !== "") {
        return `msg:${explicitId}`;
    }

    const parts = [
        toNumber(evt?.thread_id ?? evt?.threadId) ?? "",
        toNumber(evt?.apartment_id ?? evt?.apartmentId) ?? "",
        toNumber(evt?.project_id ?? evt?.projectId) ?? "",
        toNumber(evt?.sender_id ?? evt?.senderId) ?? "",
        evt?.sent_at ?? evt?.sentAt ?? "",
        typeof evt?.body === "string" ? evt.body.trim() : "",
        typeof evt?.message === "string" ? evt.message.trim() : "",
    ];

    return `fallback:${parts.join("|")}`;
};

export const buildChatNotificationUrl = (pageProps = {}, evt = {}, routeFn, overrideThread = null) => {
    if (typeof routeFn !== "function") return null;
    const thread = overrideThread || findChatNotificationThread(pageProps, evt);
    const apartmentId = toNumber(evt?.apartment_id ?? evt?.apartmentId) ?? toNumber(thread?.apartment_id);
    const apartment = getChatNotificationTargets(pageProps).apartments.find(
        (item) => toNumber(item?.id) === apartmentId
    ) || null;
    const apartmentUuid = apartment?.uuid;

    if (!apartmentUuid) return null;

    const userType = `${pageProps?.user?.type || ""}`.toLowerCase();
    const userRole = `${pageProps?.user?.currentAgencyRole || userType}`.toLowerCase();

    if (userType === "buyer") {
        return routeFn("clients.apartments.chat", { apartment: apartmentUuid });
    }

    const isManager = ['manager', 'admin', 'advisor', 'owner'].includes(userRole);
    if (isManager) {
        const params = { apartment: apartmentUuid };
        const explicitBuyerId = toNumber(evt?.buyer_id ?? evt?.buyerId);
        
        let targetBuyerId = null;
        if (thread?.buyer_id) {
            targetBuyerId = thread.buyer_id;
        } else if (explicitBuyerId) {
            targetBuyerId = explicitBuyerId;
        }

        const buyers = Array.isArray(apartment?.buyers) ? apartment.buyers : [];
        if (targetBuyerId && buyers.length > 1) {
            params.buyer = encodeBuyerId(targetBuyerId);
        }

        return routeFn("advisors.apartments.chat", params);
    }

    return null;
};

export const resolveChatNotificationPayload = async (pageProps = {}, evt = {}, routeFn) => {
    let payload = buildChatNotificationPayload(pageProps, evt, routeFn);

    const needsEnrichment =
        (payload.message === "Sent an attachment" || payload.title === "New Message") &&
        window?.axios;

    if (needsEnrichment && !payload.threadUuid) {
        const userType = `${pageProps?.user?.type || ""}`.toLowerCase();
        const isBuyer = userType === "buyer";
        const apartmentUuid = payload.apartmentUuid || payload.apartment?.uuid;
        const buyerId = toNumber(
            evt?.buyer_id ?? evt?.buyerId ?? evt?.buyer?.id ?? (isBuyer ? payload.apartment?.buyer_id : null)
        );

        if (apartmentUuid && buyerId) {
            try {
                const base = isBuyer ? "/c" : "/a";
                const response = await window.axios.get(
                    `${base}/apartments/${apartmentUuid}/buyers/${buyerId}/chat/thread`
                );
                const thread = response.data?.thread;
                if (thread?.uuid) {
                    // Re-resolve baseline payload with the fetched thread info
                    payload = buildChatNotificationPayload(pageProps, evt, routeFn, thread);
                }
            } catch (_e) {
                // Ignore fetch errors
            }
        } else if (payload.threadId) {
            // Last resort: identify thread by numeric ID if context IDs are missing
            try {
                const base = isBuyer ? "/c" : "/a";
                const response = await window.axios.get(
                    `${base}/apartments/chat/threads/${payload.threadId}/identify`
                );
                const thread = response.data?.thread;
                if (thread?.uuid) {
                    payload = buildChatNotificationPayload(pageProps, evt, routeFn, thread);
                }
            } catch (_e) {
                // Ignore identify errors
            }
        }
    }

    if (!needsEnrichment || !payload.threadUuid) {
        return payload;
    }

    try {
        const userType = `${pageProps?.user?.type || ""}`.toLowerCase();
        const base = userType === "buyer" ? "/c" : "/a";
        // Up to 10 attempts (approx 5 seconds total) to find the message and sender
        for (let attempt = 0; attempt < 10; attempt += 1) {
            await wait(attempt === 0 ? 100 : 500);

            const response = await window.axios.get(
                `${base}/apartments/chat/threads/${payload.threadUuid}/messages`,
                { params: { per_page: 5 } }
            );

            const latestMessages = response?.data?.messages?.data || [];
            const bestMessage =
                latestMessages.find((m) => {
                    const summary = summarizeChatMessage(m);
                    return summary && summary !== "Sent an attachment";
                }) || latestMessages[0];

            if (bestMessage) {
                const latestText = summarizeChatMessage(bestMessage);
                const isGeneric = latestText === "Sent an attachment";
                const hasSender = !!(bestMessage.sender_name || bestMessage.sender?.name);

                // If we found a real message body OR it's our last attempt
                if ((!isGeneric && hasSender) || attempt === 9) {
                    const projectName =
                        payload.thread?.project_name ||
                        payload.apartment?.project_name ||
                        payload.project?.name ||
                        "";
                    const apartmentName = payload.thread?.apartment_name || payload.apartment?.name || "";
                    const title = bestMessage.sender_name || bestMessage.sender?.name || payload.title;

                    return {
                        ...payload,
                        message: latestText,
                        text: [projectName, apartmentName, latestText].filter(Boolean).join(" | "),
                        title: title,
                    };
                }
            }
        }
    } catch (_error) {
        // Keep the original payload if the enrichment request fails.
    }

    return payload;
};
